"use client";

import { c as _c } from "react/compiler-runtime";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import useCreateModalState from "@/hooks/useCreateModalState";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useEffectEvent, useRef } from "react";
import UnifiedWavesListEmpty from "./UnifiedWavesListEmpty";
import { UnifiedWavesListLoader } from "./UnifiedWavesListLoader";
import type { UnifiedWavesListWavesHandle } from "./UnifiedWavesListWaves";
import UnifiedWavesListWaves from "./UnifiedWavesListWaves";
import type { MinimalWave } from "@/contexts/wave/hooks/useEnhancedWavesListCore";
interface UnifiedWavesListProps {
  readonly waves: MinimalWave[];
  readonly fetchNextPage: () => void;
  readonly hasNextPage: boolean | undefined;
  readonly isFetching: boolean;
  readonly isFetchingNextPage: boolean;
  readonly onHover: (waveId: string) => void;
  readonly scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}
const UnifiedWavesList: React.FC<UnifiedWavesListProps> = t0 => {
  const $ = _c(34);
  const {
    waves,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    onHover,
    scrollContainerRef
  } = t0;
  const {
    isApp
  } = useDeviceInfo();
  const {
    openWave
  } = useCreateModalState();
  const listRef = useRef(null);
  const hasFetchedRef = useRef(false);
  let t1;
  if ($[0] !== fetchNextPage) {
    t1 = () => {
      hasFetchedRef.current = true;
      fetchNextPage();
    };
    $[0] = fetchNextPage;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const triggerFetchNextPage = useEffectEvent(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      hasFetchedRef.current = false;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== hasNextPage || $[4] !== isFetchingNextPage) {
    t3 = [hasNextPage, isFetchingNextPage];
    $[3] = hasNextPage;
    $[4] = isFetchingNextPage;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== hasNextPage || $[7] !== isFetchingNextPage || $[8] !== triggerFetchNextPage) {
    t4 = () => {
      const sentinel = listRef.current?.sentinelRef.current;
      if (!sentinel || !hasNextPage || isFetchingNextPage) {
        return;
      }
      const cb = entries => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage && !hasFetchedRef.current) {
          triggerFetchNextPage();
        }
      };
      const obs = new IntersectionObserver(cb, {
        root: listRef.current?.containerRef.current ?? null,
        rootMargin: "100px"
      });
      obs.observe(sentinel);
      return () => obs.disconnect();
    };
    $[6] = hasNextPage;
    $[7] = isFetchingNextPage;
    $[8] = triggerFetchNextPage;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== hasNextPage || $[11] !== isFetchingNextPage) {
    t5 = [hasNextPage, isFetchingNextPage];
    $[10] = hasNextPage;
    $[11] = isFetchingNextPage;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useEffect(t4, t5);
  let t6;
  if ($[13] !== isApp || $[14] !== openWave) {
    t6 = !isApp && <div className="tw-mb-4 tw-w-full tw-px-4"><button type="button" onClick={openWave} className="tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-x-2 tw-rounded-lg tw-border-0 tw-bg-iron-800 tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-iron-300 tw-ring-1 tw-ring-inset tw-ring-iron-700 tw-transition-all tw-duration-300 desktop-hover:hover:tw-text-primary-400 desktop-hover:hover:tw-ring-iron-700"><FontAwesomeIcon icon={faPlus} className="-tw-ml-1.5 tw-size-3.5 tw-flex-shrink-0" /><span className="tw-text-xs tw-font-semibold">Create Wave</span></button></div>;
    $[13] = isApp;
    $[14] = openWave;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== onHover || $[17] !== scrollContainerRef || $[18] !== waves) {
    t7 = <UnifiedWavesListWaves ref={listRef} waves={waves} onHover={onHover} scrollContainerRef={scrollContainerRef} />;
    $[16] = onHover;
    $[17] = scrollContainerRef;
    $[18] = waves;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  const t8 = isFetching && waves.length === 0;
  let t9;
  if ($[20] !== isFetchingNextPage || $[21] !== t8) {
    t9 = <UnifiedWavesListLoader isFetching={t8} isFetchingNextPage={isFetchingNextPage} />;
    $[20] = isFetchingNextPage;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== isFetching || $[24] !== isFetchingNextPage || $[25] !== waves) {
    t10 = <UnifiedWavesListEmpty sortedWaves={waves} isFetching={isFetching} isFetchingNextPage={isFetchingNextPage} />;
    $[23] = isFetching;
    $[24] = isFetchingNextPage;
    $[25] = waves;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  let t11;
  if ($[27] !== t10 || $[28] !== t7 || $[29] !== t9) {
    t11 = <div className="tw-w-full">{t7}{t9}{t10}</div>;
    $[27] = t10;
    $[28] = t7;
    $[29] = t9;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t11 || $[32] !== t6) {
    t12 = <div className="tw-mb-4"><div className="tw-h-full tw-rounded-xl tw-bg-iron-950 tw-py-4 tw-ring-1 tw-ring-inset tw-ring-iron-800">{t6}{t11}</div></div>;
    $[31] = t11;
    $[32] = t6;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  return t12;
};
export default UnifiedWavesList;